import { type ComponentProps, useMemo, useState } from "react";
import { A4_PREVIEW_WIDTH_PX } from "@/components/ui/report/TemplateStudioEmptyCanvas/TemplateStudioEmptyCanvas";
import {
  isReportBannerImage,
  prepareReportSpecForRender,
} from "@/lib/report/prepareReportSpecForRender";
import { formatDateForUrl } from "@/lib/utils/dateUtils";
import {
  A4_PREVIEW_HEIGHT_PX,
  buildPageSpec,
  getPageableChildIds,
  getReportFooterChildIds,
  getTopLevelChildIds,
} from "./PaginatedA4Preview";
import { TemplateStudioSpecRenderer } from "./TemplateStudioSpecRenderer";

type LayoutSpec = ComponentProps<typeof TemplateStudioSpecRenderer>["spec"];
type LayoutTheme = ComponentProps<typeof TemplateStudioSpecRenderer>["theme"];

/** Renders a layout spec on one A4-width page that grows with its content. */
export const ContinuousA4Preview = ({
  spec,
  theme,
}: {
  spec: LayoutSpec;
  theme?: LayoutTheme;
}) => {
  const [todayValue] = useState(() => formatDateForUrl(new Date()));
  const preparedSpec = useMemo(
    () => prepareReportSpecForRender(spec, todayValue, "authored"),
    [spec, todayValue],
  );
  const topLevelChildIds = getTopLevelChildIds(preparedSpec);
  const footerChildIds = getReportFooterChildIds(
    preparedSpec,
    topLevelChildIds,
  );
  const pageableChildIds = getPageableChildIds(
    topLevelChildIds,
    footerChildIds,
  );
  // Leading flagged banner Images render edge to edge above the padded page.
  const firstNonBannerIndex = pageableChildIds.findIndex(
    (childId) => !isReportBannerImage(preparedSpec?.elements[childId]),
  );
  const bannerChildIds =
    firstNonBannerIndex === -1
      ? pageableChildIds
      : pageableChildIds.slice(0, firstNonBannerIndex);
  const bodyChildIds = pageableChildIds.slice(bannerChildIds.length);
  const hasLayoutChildren = topLevelChildIds.length > 0;

  return (
    <div className="flex w-full flex-col items-center pb-12">
      <div
        className="flex w-full flex-col bg-white shadow-sm"
        style={{
          minHeight: A4_PREVIEW_HEIGHT_PX,
          maxWidth: A4_PREVIEW_WIDTH_PX,
        }}
      >
        {bannerChildIds.length > 0 ? (
          <div className="shrink-0">
            <TemplateStudioSpecRenderer
              spec={buildPageSpec(preparedSpec, bannerChildIds, 2)}
              theme={theme}
            />
          </div>
        ) : null}
        <div className="flex flex-1 flex-col p-6">
          {!hasLayoutChildren ? (
            <TemplateStudioSpecRenderer spec={preparedSpec} theme={theme} />
          ) : null}
          {bodyChildIds.length > 0 ? (
            <div className="shrink-0">
              <TemplateStudioSpecRenderer
                spec={buildPageSpec(preparedSpec, bodyChildIds, 0)}
                theme={theme}
              />
            </div>
          ) : null}
          {footerChildIds.length > 0 ? (
            <div className="mt-auto shrink-0 pt-4">
              <TemplateStudioSpecRenderer
                spec={buildPageSpec(preparedSpec, footerChildIds, 1)}
                theme={theme}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
